import { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem('ls_admin_token') || '');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [tab, setTab] = useState('cities');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      axios.get(`${API}/stats`).then(r => setStats(r.data)).catch(() => {});
    }
  }, [token]);

  const login = async () => {
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem('ls_admin_token', res.data.token);
      setToken(res.data.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
      setLoginError('');
    } catch { setLoginError('Invalid credentials'); }
  };

  const logout = () => {
    localStorage.removeItem('ls_admin_token');
    setToken('');
    delete axios.defaults.headers.common['Authorization'];
  };

  if (!token) {
    return (
      <div data-testid="admin-login" className="ls-section">
        <div className="ls-container max-w-md mx-auto">
          <div className="ls-card">
            <h1 className="text-2xl font-bold mb-6 text-center">Admin Login</h1>
            <div className="space-y-4">
              <input data-testid="admin-username" value={username} onChange={e => setUsername(e.target.value)} placeholder="Username" className="w-full p-3 border border-[var(--ls-border)] rounded-lg" />
              <input data-testid="admin-password" type="password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && login()} placeholder="Password" className="w-full p-3 border border-[var(--ls-border)] rounded-lg" />
              {loginError && <p className="text-red-500 text-sm">{loginError}</p>}
              <button data-testid="admin-login-btn" onClick={login} className="ls-btn-gold w-full">Login</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'cities', label: 'Cities & Countries', icon: 'fa-globe' },
    { id: 'blog', label: 'Blog', icon: 'fa-newspaper' },
    { id: 'banners', label: 'Banners', icon: 'fa-rectangle-ad' },
    { id: 'indexnow', label: 'IndexNow', icon: 'fa-bolt' },
    { id: 'settings', label: 'Settings', icon: 'fa-gear' },
  ];

  return (
    <div data-testid="admin-page" className="ls-section">
      <div className="ls-container">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Admin Panel</h1>
          <div className="flex items-center gap-4">
            {stats && <span className="text-sm text-[var(--ls-text-light)]">Total pages: <strong className="text-[var(--ls-gold)]">{stats.total_pages}</strong></span>}
            <button onClick={logout} className="text-sm text-red-500 hover:text-red-700"><i className="fa-solid fa-right-from-bracket mr-1"></i>Logout</button>
          </div>
        </div>
        <div className="flex gap-2 mb-6 flex-wrap">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${tab === t.id ? 'bg-[var(--ls-navy)] text-white' : 'bg-white text-[var(--ls-text)] hover:bg-[var(--ls-bg)]'}`}>
              <i className={`fa-solid ${t.icon}`}></i> {t.label}
            </button>
          ))}
        </div>
        {tab === 'cities' && <CitiesTab />}
        {tab === 'blog' && <BlogTab />}
        {tab === 'banners' && <BannersTab />}
        {tab === 'indexnow' && <IndexNowTab />}
        {tab === 'settings' && <SettingsTab />}
      </div>
    </div>
  );
}

function CitiesTab() {
  const [cities, setCities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [csv, setCsv] = useState('');
  const [csvType, setCsvType] = useState('cities');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    axios.get(`${API}/cities?limit=500`).then(r => setCities(r.data.cities || []));
    axios.get(`${API}/countries?limit=500`).then(r => setCountries(r.data.countries || []));
  }, []);

  const uploadCSV = async () => {
    const lines = csv.trim().split('\n').filter(l => l.trim());
    const items = lines.map(l => {
      const parts = l.split(',').map(p => p.trim());
      if (csvType === 'cities') return { name: parts[0], slug: parts[0].toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''), region: parts[1] || '', province: parts[2] || '', population: parts[3] || '' };
      return { name: parts[0], slug: parts[0].toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''), region: parts[1] || '', flag_emoji: parts[2] || '', ofw_estimate: parts[3] || '' };
    });
    try {
      const endpoint = csvType === 'cities' ? 'cities' : 'countries';
      const key = csvType === 'cities' ? 'cities' : 'countries';
      const res = await axios.post(`${API}/admin/${endpoint}/bulk`, { [key]: items });
      setMsg(`Added ${res.data.added} ${csvType}. Total: ${res.data.total}`);
      setCsv('');
      if (csvType === 'cities') axios.get(`${API}/cities?limit=500`).then(r => setCities(r.data.cities || []));
      else axios.get(`${API}/countries?limit=500`).then(r => setCountries(r.data.countries || []));
    } catch (e) { setMsg('Error: ' + (e.response?.data?.detail || e.message)); }
  };

  return (
    <div>
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="ls-card"><h3 className="font-bold text-lg mb-1">Cities</h3><p className="text-3xl font-bold text-[var(--ls-gold)]">{cities.length}</p></div>
        <div className="ls-card"><h3 className="font-bold text-lg mb-1">Countries</h3><p className="text-3xl font-bold text-[var(--ls-gold)]">{countries.length}</p></div>
      </div>
      <div className="ls-card mb-6">
        <h3 className="font-bold mb-4">Add via CSV</h3>
        <div className="flex gap-4 mb-4">
          <label className="flex items-center gap-2"><input type="radio" checked={csvType === 'cities'} onChange={() => setCsvType('cities')} /> Cities</label>
          <label className="flex items-center gap-2"><input type="radio" checked={csvType === 'countries'} onChange={() => setCsvType('countries')} /> Countries</label>
        </div>
        <p className="text-xs text-[var(--ls-text-light)] mb-2">
          {csvType === 'cities' ? 'Format: City Name, Region, Province, Population' : 'Format: Country Name, Region, Flag Code, OFW Estimate'}
        </p>
        <textarea data-testid="csv-input" value={csv} onChange={e => setCsv(e.target.value)} rows={6}
          className="w-full p-3 border border-[var(--ls-border)] rounded-lg text-sm font-mono mb-3" placeholder={csvType === 'cities' ? 'Antipolo, CALABARZON, Rizal, 887,399' : 'Greece, Europe, GR, 10,000+'} />
        <button onClick={uploadCSV} className="ls-btn-gold text-sm" data-testid="csv-upload-btn">Upload {csvType}</button>
        {msg && <p className="mt-2 text-sm text-[var(--ls-success)]">{msg}</p>}
      </div>
    </div>
  );
}

function BlogTab() {
  const [posts, setPosts] = useState([]);
  const [title, setTitle] = useState('');
  const [generating, setGenerating] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { loadPosts(); }, []);
  const loadPosts = () => axios.get(`${API}/blog?limit=100`).then(r => setPosts(r.data.posts || []));

  const generate = async () => {
    if (!title.trim()) return;
    setGenerating(true); setMsg('');
    try {
      await axios.post(`${API}/admin/blog/generate`, { title: title.trim() });
      setMsg(`Generated: "${title}"`);
      setTitle('');
      loadPosts();
    } catch (e) { setMsg('Error: ' + (e.response?.data?.detail || e.message)); }
    setGenerating(false);
  };

  const schedule52 = async () => {
    setScheduling(true); setMsg('Generating 52-week blog supply... This takes a few minutes...');
    try {
      const res = await axios.post(`${API}/admin/blog/schedule`, { count: 52 });
      setMsg(`Generated ${res.data.generated} posts! Scheduled for weekly publishing.`);
      loadPosts();
    } catch (e) { setMsg('Error: ' + (e.response?.data?.detail || e.message)); }
    setScheduling(false);
  };

  const deletePost = async (id) => {
    await axios.delete(`${API}/admin/blog/${id}`);
    loadPosts();
  };

  const SUGGESTED = [
    "5 Things Every OFW Should Know About Estate Planning",
    "How to Create a Special Power of Attorney While Working Abroad",
    "Guardian Mandate: Protecting Your Children as an OFW",
    "Why Digital Vault is Essential for Filipino Families",
    "Understanding Philippine Inheritance Law for OFWs",
    "How to Sell Property in the Philippines from Saudi Arabia",
    "Estate Planning Mistakes Every OFW Should Avoid",
    "The No-Flight Permission: Managing Your Negosyo from Abroad",
  ];

  return (
    <div>
      <div className="ls-card mb-6">
        <h3 className="font-bold mb-4">AI Blog Generator</h3>
        <input data-testid="blog-title-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="Enter blog post title..."
          className="w-full p-3 border border-[var(--ls-border)] rounded-lg text-sm mb-3" />
        <button onClick={generate} disabled={generating} className="ls-btn-gold text-sm" data-testid="blog-generate-btn">
          {generating ? <><i className="fa-solid fa-spinner fa-spin mr-2"></i>Generating...</> : 'Generate Blog Post'}
        </button>
        {msg && <p className="mt-2 text-sm text-[var(--ls-success)]">{msg}</p>}
        <div className="mt-4">
          <p className="text-xs font-medium text-[var(--ls-text-light)] mb-2">Suggested titles:</p>
          <div className="flex flex-wrap gap-2">
            {SUGGESTED.map((s, i) => (
              <button key={i} onClick={() => setTitle(s)} className="text-xs px-3 py-1 bg-[var(--ls-bg)] rounded-full hover:bg-[var(--ls-gold)] hover:text-white transition-colors">{s}</button>
            ))}
          </div>
        </div>
      </div>
      <div className="ls-card mb-6 bg-[var(--ls-bg)]">
        <h3 className="font-bold mb-3">52-Week Auto Blog Scheduler</h3>
        <p className="text-sm text-[var(--ls-text-light)] mb-3">Generate 52 blog posts scheduled 1 per week. Only published posts appear on the blog.</p>
        <button onClick={schedule52} disabled={scheduling || generating} className="ls-btn-navy text-sm" data-testid="blog-schedule-btn">
          {scheduling ? <><i className="fa-solid fa-spinner fa-spin mr-2"></i>Generating...</> : <><i className="fa-solid fa-calendar-plus mr-2"></i>Generate 52-Week Supply</>}
        </button>
      </div>
      <div className="ls-card">
        <h3 className="font-bold mb-4">Published Posts ({posts.length})</h3>
        <div className="space-y-3">
          {posts.map(p => (
            <div key={p.id} className="flex justify-between items-center p-3 bg-[var(--ls-bg)] rounded-lg">
              <div>
                <p className="font-medium text-sm">{p.title}</p>
                <p className="text-xs text-[var(--ls-text-light)]">{new Date(p.published_at).toLocaleDateString()}</p>
              </div>
              <button onClick={() => deletePost(p.id)} className="text-red-500 text-sm hover:text-red-700"><i className="fa-solid fa-trash"></i></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BannersTab() {
  const [banners, setBanners] = useState([]);
  const [form, setForm] = useState({ title: '', link_url: '', position: 'sidebar' });

  useEffect(() => { loadBanners(); }, []);
  const loadBanners = () => axios.get(`${API}/banners`).then(r => setBanners(r.data.banners || []));

  const create = async () => {
    if (!form.title || !form.link_url) return;
    await axios.post(`${API}/admin/banners`, form);
    setForm({ title: '', link_url: '', position: 'sidebar' });
    loadBanners();
  };

  const deleteBanner = async (id) => {
    await axios.delete(`${API}/admin/banners/${id}`);
    loadBanners();
  };

  return (
    <div>
      <div className="ls-card mb-6">
        <h3 className="font-bold mb-4">Add Banner</h3>
        <div className="space-y-3">
          <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Banner title" className="w-full p-3 border border-[var(--ls-border)] rounded-lg text-sm" />
          <input value={form.link_url} onChange={e => setForm({ ...form, link_url: e.target.value })} placeholder="Link URL" className="w-full p-3 border border-[var(--ls-border)] rounded-lg text-sm" />
          <button onClick={create} className="ls-btn-gold text-sm">Add Banner</button>
        </div>
      </div>
      <div className="ls-card">
        <h3 className="font-bold mb-4">Active Banners ({banners.length})</h3>
        {banners.map(b => (
          <div key={b.id} className="flex justify-between items-center p-3 bg-[var(--ls-bg)] rounded-lg mb-2">
            <div><p className="font-medium text-sm">{b.title}</p><p className="text-xs text-[var(--ls-text-light)]">{b.link_url}</p></div>
            <button onClick={() => deleteBanner(b.id)} className="text-red-500 text-sm"><i className="fa-solid fa-trash"></i></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function IndexNowTab() {
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (type) => {
    setSubmitting(true);
    try {
      let urls = [];
      if (type === 'all' || type === 'cities') {
        const res = await axios.get(`${API}/cities?limit=500`);
        urls.push(...(res.data.cities || []).map(c => `https://legasecure.net/city/${c.slug}`));
      }
      if (type === 'all' || type === 'countries') {
        const res = await axios.get(`${API}/countries?limit=500`);
        const countries = res.data.countries || [];
        urls.push(...countries.map(c => `https://legasecure.net/ofw/${c.slug}`));
        urls.push(...countries.map(c => `https://legasecure.net/spa/${c.slug}`));
      }
      if (type === 'all') {
        urls.push('https://legasecure.net/', 'https://legasecure.net/all-cities', 'https://legasecure.net/all-countries', 'https://legasecure.net/compare', 'https://legasecure.net/blog');
        const guides = await axios.get(`${API}/guides`);
        urls.push(...(guides.data.guides || []).map(g => `https://legasecure.net/guides/${g.slug}`));
        const blog = await axios.get(`${API}/blog?limit=500`);
        urls.push(...(blog.data.posts || []).map(p => `https://legasecure.net/blog/${p.slug}`));
      }
      const res = await axios.post(`${API}/admin/indexnow`, { urls });
      setResult(res.data);
    } catch (e) { setResult({ error: e.message }); }
    setSubmitting(false);
  };

  return (
    <div className="ls-card">
      <h3 className="font-bold mb-4">IndexNow Submission</h3>
      <p className="text-sm text-[var(--ls-text-light)] mb-4">Submit URLs to Bing, Yahoo, Yandex, and DuckDuckGo for faster indexing.</p>
      <div className="flex gap-3 flex-wrap mb-4">
        <button onClick={() => submit('all')} disabled={submitting} className="ls-btn-gold text-sm" data-testid="indexnow-all">
          {submitting ? 'Submitting...' : 'Submit All Pages'}
        </button>
        <button onClick={() => submit('cities')} disabled={submitting} className="ls-btn-outline text-sm">Cities Only</button>
        <button onClick={() => submit('countries')} disabled={submitting} className="ls-btn-outline text-sm">Countries Only</button>
      </div>
      {result && (
        <div className="bg-[var(--ls-bg)] p-4 rounded-lg text-sm">
          <pre className="whitespace-pre-wrap">{JSON.stringify(result, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

function SettingsTab() {
  const [settings, setSettings] = useState({ site_name: '', contact_email: '', adsense_enabled: false });
  const [saved, setSaved] = useState(false);

  useEffect(() => { axios.get(`${API}/settings`).then(r => setSettings(r.data)); }, []);

  const save = async () => {
    await axios.put(`${API}/admin/settings`, settings);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="ls-card">
      <h3 className="font-bold mb-4">Site Settings</h3>
      <div className="space-y-4 max-w-md">
        <div>
          <label className="text-sm font-medium mb-1 block">Site Name</label>
          <input value={settings.site_name} onChange={e => setSettings({ ...settings, site_name: e.target.value })} className="w-full p-3 border border-[var(--ls-border)] rounded-lg text-sm" />
        </div>
        <div>
          <label className="text-sm font-medium mb-1 block">Contact Email</label>
          <input value={settings.contact_email} onChange={e => setSettings({ ...settings, contact_email: e.target.value })} className="w-full p-3 border border-[var(--ls-border)] rounded-lg text-sm" />
        </div>
        <div className="flex items-center gap-3">
          <input type="checkbox" checked={settings.adsense_enabled} onChange={e => setSettings({ ...settings, adsense_enabled: e.target.checked })} />
          <label className="text-sm">Enable AdSense</label>
        </div>
        <button onClick={save} className="ls-btn-gold text-sm">{saved ? 'Saved!' : 'Save Settings'}</button>
      </div>
    </div>
  );
}
